import {
  Component,
  Inject,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { AlertDialogService } from "../../components/_alert-dialog/alert-dialog.service";
import { Mouvement, MouvementPreview } from "../../interfaces/Mouvement";
import { MouvementDialogService } from "./mouvement-dialog.service";
import { Actionnaire } from "../../interfaces/Actionnaire";
import { ToolsService } from "../../services/tools.service";

@Component({
  selector: "mouvement-dialog",
  templateUrl: "mouvement-dialog.html",
  standalone: false,
})
export class MouvementDialogComponent implements OnInit, AfterViewInit {
  mouvement: Mouvement;
  preview: MouvementPreview | null = null;
  isLoading: boolean = false;
  showPreview: boolean = false;
  counterparty: Actionnaire | null = null;
  private isGeneratingPreview: boolean = false;
  private lastActionnaireId: number = 0;
  private lastFromId: number = 0;
  isInitializing: boolean = true;
  isTodoMouvement: boolean = false;

  typesOptions = [
    { value: 0, label: "Achat d'actions" },
    { value: 1, label: "Vente d'actions" },
    { value: 2, label: "Donation d'actions en PP" },
    { value: 3, label: "Donation d'actions en NP" },
    { value: 4, label: "Transmission par succession" },
    { value: 5, label: "Transfert de nue propriété" },
    { value: 6, label: "Transfert d'usufruit" },
  ];

  constructor(
    public _dr: MatDialogRef<MouvementDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      mouvement_ID: number;
      actionnaire_ID: number;
      deleteMode?: boolean;
    },
    public _s: MouvementDialogService,
    private _cdr: ChangeDetectorRef,
    private _tools: ToolsService,
    private _alert: AlertDialogService
  ) {}

  ngOnInit(): void {
    this.initMouvement();
    // Si on est en mode suppression, déclencher automatiquement la prévisualisation
    if (this.data.deleteMode && this.data.mouvement_ID > 0) {
      setTimeout(() => {
        this.deleteMouvement();
      }, 100);
    }
  }

  ngAfterViewInit(): void {
    // Forcer la détection de changement après l'initialisation de la vue
    // S'assurer que le radio button "Auprès de la société" est coché par défaut lors de la création
    setTimeout(() => {
      if (this.mouvement && this.mouvement.mouvement_ID === 0) {
        // S'assurer que la valeur est bien à 1 (société) - forcer explicitement
        if (this.mouvement.mouvement_Origine !== 1) {
          this.mouvement.mouvement_Origine = 1;
        }
        this._cdr.detectChanges();
      }
      // Maintenant on peut mettre isInitializing à false
      this.isInitializing = false;
    }, 200);
  }

  initMouvement(): void {
    if (this.data.mouvement_ID === 0) {
      this.mouvement = {
        mouvement_ID: 0,
        actionnaire_ID: this.data.actionnaire_ID || 0,
        from_ID: 0,
        mouvement_Type: 0,
        mouvement_Origine: 1, // Par défaut: société
        mouvement_Date: this._tools.dateUTC(
          new Date(new Date().setHours(0, 0, 0, 0))
        ),
        mouvement_Nature: "",
        mouvement_Sens: 1,
        mouvement_Valeur: 0,
        mouvement_AC1PP: 0,
        mouvement_AC1NP: 0,
        mouvement_AC1US: 0,
        mouvement_AC2PP: 0,
        mouvement_AC2NP: 0,
        mouvement_AC2US: 0,
        mouvement_TODO: 0,
        mouvement_DateTODO: null,
        mouvement_ID_Connexe: null,
      };
      // Initialiser les valeurs de suivi
      this.lastActionnaireId = this.mouvement.actionnaire_ID;
      this.lastFromId = this.mouvement.from_ID;
      // S'assurer que mouvement_Origine est bien à 1 (société) par défaut
      this.mouvement.mouvement_Origine = 1;
      // Initialiser les valeurs TODO
      this.mouvement.mouvement_TODO = 0;
      this.mouvement.mouvement_DateTODO = null;
      this.isTodoMouvement = false;
      // Mettre à jour la nature à l'initialisation
      setTimeout(() => {
        this.updateNature();
      }, 0);
      // Ne pas générer l'aperçu à l'initialisation car les champs ne sont pas encore remplis
    } else {
      this.getMouvement();
    }
  }

  get isFromActionnaireDialog(): boolean {
    return this.data.actionnaire_ID > 0;
  }

  get isEditMode(): boolean {
    return this.mouvement?.mouvement_ID > 0;
  }

  getMouvement(): void {
    this.isLoading = true;
    this._s.get(this.data.mouvement_ID, (mouvement: Mouvement) => {
      this.mouvement = mouvement;
      // Initialiser isTodoMouvement basé sur mouvement_TODO (-1 = TODO, 0 = normal)
      this.isTodoMouvement = this.mouvement.mouvement_TODO === -1;
      // Initialiser les valeurs de suivi
      this.lastActionnaireId = this.mouvement.actionnaire_ID;
      this.lastFromId = this.mouvement.from_ID;
      this.isLoading = false;
      this.isInitializing = false;
      // Générer l'aperçu automatiquement après chargement
      setTimeout(() => this.checkAndGeneratePreview(), 100);
    });
  }

  onTodoChange(): void {
    // Mettre à jour mouvement_TODO : -1 si TODO, 0 sinon
    this.mouvement.mouvement_TODO = this.isTodoMouvement ? -1 : 0;
    // Si on coche, attribuer la date du jour par défaut
    if (this.isTodoMouvement) {
      if (!this.mouvement.mouvement_DateTODO) {
        this.mouvement.mouvement_DateTODO = this._tools.dateUTC(
          new Date(new Date().setHours(0, 0, 0, 0))
        );
      }
    } else {
      // Si on décoche, vider la date
      this.mouvement.mouvement_DateTODO = null;
    }
  }

  onActionnaireChange(actionnaire_ID: number): void {
    // Éviter les appels inutiles si la valeur n'a pas changé
    if (this.lastActionnaireId === actionnaire_ID) return;
    this.lastActionnaireId = actionnaire_ID;
    this.mouvement.actionnaire_ID = actionnaire_ID;
    // Utiliser setTimeout pour éviter les appels en cascade
    setTimeout(() => this.checkAndGeneratePreview(), 0);
  }

  onFromChange(from_ID: number): void {
    // Éviter les appels inutiles si la valeur n'a pas changé
    if (this.lastFromId === from_ID) {
      // Mettre à jour quand même la valeur au cas où
      this.mouvement.from_ID = from_ID;
      return;
    }
    this.lastFromId = from_ID;
    this.mouvement.from_ID = from_ID;
    this.counterparty = null;
    this.updateNature();
    // Utiliser setTimeout pour éviter les appels en cascade
    setTimeout(() => this.checkAndGeneratePreview(), 0);
  }

  onOrigineChange(value: number): void {
    if (this.isEditMode) return; // Bloqué en modification

    // Ignorer complètement les appels pendant l'initialisation
    if (this.isInitializing) {
      // Mais s'assurer que la valeur reste à 1 si c'est une création
      if (this.mouvement.mouvement_ID === 0 && value !== 1) {
        // Ne pas changer la valeur, la forcer à 1
        setTimeout(() => {
          this.mouvement.mouvement_Origine = 1;
          this._cdr.detectChanges();
        }, 0);
      }
      return;
    }

    // Éviter les appels inutiles si la valeur n'a pas changé
    if (this.mouvement.mouvement_Origine === value) return;

    // Mettre à jour la valeur
    this.mouvement.mouvement_Origine = value;

    // Si on sélectionne "Auprès de la société" mais que ce n'est pas autorisé, forcer à 0
    if (value === 1 && !this.isSocieteAllowed) {
      // Utiliser setTimeout pour éviter les problèmes de binding
      setTimeout(() => {
        this.mouvement.mouvement_Origine = 0;
      }, 0);
      return;
    }

    // Si on passe à "Auprès de la société", réinitialiser from_ID
    if (value === 1) {
      this.mouvement.from_ID = 0;
      this.counterparty = null;
    } else if (value === 0) {
      // Si on passe à "Auprès d'un autre actionnaire", réinitialiser from_ID aussi
      this.mouvement.from_ID = 0;
      this.counterparty = null;
    }

    this.updateNature();
    this.checkAndGeneratePreview();
  }

  onTypeChange(): void {
    if (this.isEditMode) return; // Bloqué en modification

    // Si le type ne permet pas la société et que la société est sélectionnée, forcer à 0
    if (!this.isSocieteAllowed && this.mouvement.mouvement_Origine === 1) {
      this.mouvement.mouvement_Origine = 0;
    }

    this.updateNature();
    this.checkAndGeneratePreview();
  }

  onValeurChange(): void {
    this.checkAndGeneratePreview();
  }

  onCounterpartySelectionChange(a: Actionnaire | null): void {
    // Éviter les appels inutiles si l'actionnaire n'a pas changé
    const newFromId = a?.actionnaire_ID || 0;
    if (this.lastFromId === newFromId && this.counterparty === a) {
      return;
    }

    this.lastFromId = newFromId;
    this.mouvement.from_ID = newFromId;
    this.counterparty = a;
    this.updateNature();
    // Ne pas appeler checkAndGeneratePreview ici car onFromChange l'a déjà fait
    // onFromChange sera appelé en premier via ngModelChange
  }

  checkAndGeneratePreview(): void {
    // Éviter les appels en boucle
    if (this.isGeneratingPreview || this.isLoading) {
      return;
    }

    // Vérifier si tous les champs nécessaires sont remplis
    if (!this.mouvement) {
      this.showPreview = false;
      return;
    }

    // Champs obligatoires de base
    if (!this.mouvement.actionnaire_ID || this.mouvement.actionnaire_ID <= 0) {
      this.showPreview = false;
      return;
    }
    if (!this.mouvement.mouvement_Date) {
      this.showPreview = false;
      return;
    }
    if (
      !this.mouvement.mouvement_Valeur ||
      this.mouvement.mouvement_Valeur <= 0
    ) {
      this.showPreview = false;
      return;
    }

    // Si destinataire = autre actionnaire, vérifier que from_ID est renseigné
    if (this.mouvement.mouvement_Origine === 0) {
      if (!this.mouvement.from_ID || this.mouvement.from_ID <= 0) {
        this.showPreview = false;
        return;
      }
    }

    // Générer l'aperçu automatiquement
    this.generatePreview(true);
  }

  get isSocieteAllowed(): boolean {
    // Delphi: 2,3,5 => société non dispo. On étend à 4,6 comme demandé (contrep. actionnaire requise).
    return ![2, 3, 4, 5, 6].includes(this.mouvement.mouvement_Type);
  }

  private updateNature(): void {
    if (!this.mouvement) return;

    const type = this.mouvement.mouvement_Type;
    const isSoc = this.mouvement.mouvement_Origine === 1;
    const otherName = this.counterparty
      ? `${this.counterparty.actionnaire_Nom} - ${this.counterparty.actionnaire_Prenom}`.trim()
      : "";

    if (isSoc) {
      if (type === 0)
        this.mouvement.mouvement_Nature = "Achat d'actions de la société";
      else if (type === 1)
        this.mouvement.mouvement_Nature = "Vente d'actions à la société";
      else this.mouvement.mouvement_Nature = ""; // Types 2-6 ne peuvent pas être avec société
      return;
    }

    // Si pas de contrepartie et pas société, ne pas mettre à jour la nature
    if (!otherName && this.mouvement.mouvement_Origine === 0) {
      this.mouvement.mouvement_Nature = "";
      return;
    }

    switch (type) {
      case 0:
        this.mouvement.mouvement_Nature = `Achat d'actions de ${otherName}`;
        break;
      case 1:
        this.mouvement.mouvement_Nature = `Vente d'actions à ${otherName}`;
        break;
      case 2:
        this.mouvement.mouvement_Nature = `Donation d'actions en PP à ${otherName}`;
        break;
      case 3:
        this.mouvement.mouvement_Nature = `Donation d'actions en NP à ${otherName}`;
        break;
      case 4:
        this.mouvement.mouvement_Nature = `Transmission par succession à ${otherName}`;
        break;
      case 5:
        this.mouvement.mouvement_Nature = `Transfert de nue propriété à ${otherName}`;
        break;
      case 6:
        this.mouvement.mouvement_Nature = `Transfert d'usufruit à ${otherName}`;
        break;
      default:
        this.mouvement.mouvement_Nature = "";
        break;
    }
  }

  generatePreview(silent: boolean = false): void {
    // Éviter les appels en boucle
    if (this.isGeneratingPreview || this.isLoading) return;
    this.isGeneratingPreview = true;

    // Ne pas afficher d'erreur si appelé automatiquement
    if (!silent) {
      if (!this.mouvement.actionnaire_ID) {
        this.isGeneratingPreview = false;
        this._alert.openDialog(
          "Erreur",
          { message: "Vous devez sélectionner un actionnaire" },
          "error"
        );
        return;
      }

      if (this.mouvement.mouvement_Origine === 0 && !this.mouvement.from_ID) {
        this.isGeneratingPreview = false;
        this._alert.openDialog(
          "Erreur",
          { message: "Vous devez sélectionner un second actionnaire" },
          "error"
        );
        return;
      }

      if (
        !this.mouvement.mouvement_Valeur ||
        this.mouvement.mouvement_Valeur <= 0
      ) {
        this.isGeneratingPreview = false;
        this._alert.openDialog(
          "Erreur",
          { message: "Vous devez saisir un nombre d'actions valide" },
          "error"
        );
        return;
      }
    }

    this.isLoading = true;
    this._s.preview(this.mouvement, (preview: MouvementPreview) => {
      this.preview = preview;
      this.showPreview = true;
      this.isLoading = false;
      this.isGeneratingPreview = false;
    });
  }

  postMouvement(): void {
    // Vérifier que l'aperçu est valide
    if (!this.showPreview || !this.preview) {
      this.checkAndGeneratePreview();
      if (!this.showPreview || !this.preview) {
        this._alert.openDialog(
          "Erreur",
          { message: "Veuillez remplir tous les champs nécessaires" },
          "error"
        );
        return;
      }
    }

    if (!this.preview.isValid) {
      this._alert.openDialog(
        "Erreur",
        { message: this.preview.errorMessage || "Transaction invalide" },
        "error"
      );
      return;
    }

    this._alert.openConfirm(
      "Voulez-vous vraiment enregistrer ce mouvement ?",
      () => {
        this.isLoading = true;
        this._s.post(this.mouvement, () => {
          this.isLoading = false;
          this._dr.close(true);
        });
      }
    );
  }

  close(): void {
    this._dr.close(false);
  }

  get needsSecondActionnaire(): boolean {
    return this.mouvement.mouvement_Origine === 0;
  }

  isDeletePreviewMode: boolean = false;

  deleteMouvement(): void {
    if (!this.mouvement || this.mouvement.mouvement_ID === 0) {
      return;
    }

    // Si c'est un mouvement futur (TODO), supprimer directement sans prévisualisation
    // car les positions d'actions n'ont jamais été modifiées
    if (this.isTodoMouvement || this.mouvement.mouvement_TODO === -1) {
      this._alert.openConfirm(
        "Vous vous apprêtez à supprimer un mouvement prévisionnel. Êtes-vous sûr de vouloir effectuer cette action ?",
        () => {
          this.isLoading = true;
          this._s.delete(this.mouvement.mouvement_ID, () => {
            this.isLoading = false;
            this._dr.close(true);
          });
        }
      );
      return;
    }

    // Pour les mouvements effectifs, afficher la prévisualisation de suppression
    this.isLoading = true;
    this._s.deletePreview(
      this.mouvement.mouvement_ID,
      (preview: MouvementPreview) => {
        this.isLoading = false;

        if (!preview.isValid) {
          // Si on est en mode suppression et que la prévisualisation échoue, fermer le dialog
          if (this.data.deleteMode) {
            this._alert.openDialog(
              "Erreur",
              {
                message:
                  preview.errorMessage ||
                  "Impossible de supprimer ce mouvement",
              },
              "error"
            );
            this._dr.close(false);
            return;
          } else {
            this._alert.openDialog(
              "Erreur",
              {
                message:
                  preview.errorMessage ||
                  "Impossible de supprimer ce mouvement",
              },
              "error"
            );
            return;
          }
        }

        // Afficher la prévisualisation dans le dialog
        this.preview = preview;
        this.showPreview = true;
        this.isDeletePreviewMode = true; // Activer le mode prévisualisation de suppression
      }
    );
  }

  confirmDelete(): void {
    if (!this.mouvement || this.mouvement.mouvement_ID === 0) {
      return;
    }

    this.isLoading = true;
    this._s.delete(this.mouvement.mouvement_ID, () => {
      this.isLoading = false;
      this._dr.close(true);
    });
  }

  cancelDelete(): void {
    this.isDeletePreviewMode = false;
    this.showPreview = false;
    this.preview = null;
  }

  actionnerMouvement(): void {
    if (
      !this.mouvement ||
      this.mouvement.mouvement_ID === 0 ||
      !this.isTodoMouvement
    ) {
      return;
    }

    this._alert.openConfirm(
      "Vous vous apprêtez à actionner un mouvement pré-enregistré. Êtes-vous sûr de vouloir faire cette action ?",
      () => {
        this.isLoading = true;
        this._s.actionner(this.mouvement.mouvement_ID, () => {
          this.isLoading = false;
          this._dr.close(true);
        });
      }
    );
  }
}
